import { useState, useRef, useEffect, useCallback } from 'react';
import { Text, Button, Badge, Input, Spinner, ToggleButton } from '@fluentui/react-components';
import {
  ChatRegular, SendRegular, DeleteRegular,
  BrainCircuitRegular, FolderRegular, CloudRegular,
} from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import { MarkdownMessage } from '../chat-shell/MarkdownMessage';
import { MessageBubble } from '../chat-shell/MessageBubble';
import type { Project } from '../services/project-service';
import { useApiToken } from '../hooks/useApiToken';
import { apiUrl } from '../utils/api-url';

type ChatMode = 'project' | 'case' | 'general';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatPageProps {
  project?: Project | null;
}

const MODE_CONFIG: Record<ChatMode, { icon: typeof FolderRegular; colour: 'brand' | 'success' | 'informative'; labelKey: string; descriptionKey: string }> = {
  project: { icon: FolderRegular, colour: 'brand', labelKey: 'chat.mode.project.label', descriptionKey: 'chat.mode.project.description' },
  case: { icon: BrainCircuitRegular, colour: 'success', labelKey: 'chat.mode.case.label', descriptionKey: 'chat.mode.case.description' },
  general: { icon: CloudRegular, colour: 'informative', labelKey: 'chat.mode.general.label', descriptionKey: 'chat.mode.general.description' },
};

export function ChatPage({ project }: ChatPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [mode, setMode] = useState<ChatMode>(project ? 'project' : 'general');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Reset messages when mode changes
  useEffect(() => {
    const config = MODE_CONFIG[mode];
    const label = intl.formatMessage({ id: config.labelKey });
    const description = intl.formatMessage({ id: config.descriptionKey });
    setMessages([{
      role: 'system',
      content: `**${label} Mode** — ${description}${project ? ` (${project.name})` : ''}`,
    }]);
  }, [mode, project]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);

    // Add empty assistant message for streaming
    const assistantIdx = messages.length + 1;
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const token = await getToken();
      let endpoint: string;
      let body: Record<string, unknown>;

      if (mode === 'case' && project) {
        endpoint = `/api/stream/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`;
        body = { message: text };
      } else {
        endpoint = '/api/chat/general';
        body = {
          message: text,
          mode,
          customerId: project?.customerId,
          projectId: project?.id,
          conversationHistory: messages
            .filter(m => m.role !== 'system')
            .slice(-10)
            .map(m => ({ role: m.role, content: m.content })),
        };
      }

      const res = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (res.ok && res.headers.get('content-type')?.includes('text/event-stream')) {
        // Stream SSE
        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        if (reader) {
          let currentEvent = 'delta';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split('\n')) {
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                try {
                  const parsed = JSON.parse(line.slice(6));

                  if (currentEvent === 'tool_call' && parsed.tool && parsed.summary) {
                    // Show tool call as an inline notification in the assistant message
                    const toolNote = `\n\n> **${parsed.summary}**\n\n`;
                    accumulated += toolNote;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  } else if (parsed.text) {
                    accumulated += parsed.text;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  }
                } catch { /* skip */ }
              }
            }
          }
        }
      } else if (res.ok) {
        // Non-streaming response
        const data = await res.json();
        const reply = data.reply || data.message || data.content || JSON.stringify(data);
        setMessages(prev => {
          const updated = [...prev];
          if (updated[assistantIdx]) {
            updated[assistantIdx] = { ...updated[assistantIdx], content: reply };
          }
          return updated;
        });
      } else {
        throw new Error(`API error: ${res.status}`);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
      }]);
    } finally {
      setSending(false);
    }
  }, [getToken, messages, mode, project, sending]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleReset = () => {
    const config = MODE_CONFIG[mode];
    const label = intl.formatMessage({ id: config.labelKey });
    const description = intl.formatMessage({ id: config.descriptionKey });
    setMessages([{
      role: 'system',
      content: `**${label} Mode** — ${intl.formatMessage({ id: 'chat.reset.notice' })} ${description}`,
    }]);
  };

  return (
    <div className="page-root chat-page">
      <div className="page-title-bar">
        <ChatRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'chat.title' })}</Text>
        <div className="title-bar-actions">
          {/* Mode toggle — like Microsoft Work/Personal */}
          <div className="chat-mode-toggle">
            {(Object.keys(MODE_CONFIG) as ChatMode[]).map(m => {
              const config = MODE_CONFIG[m];
              const Icon = config.icon;
              const isDisabled = (m === 'project' || m === 'case') && !project;
              const modeLabel = intl.formatMessage({ id: config.labelKey });
              const modeDescription = intl.formatMessage({ id: config.descriptionKey });
              return (
                <ToggleButton
                  key={m}
                  size="small"
                  appearance={mode === m ? 'primary' : 'outline'}
                  checked={mode === m}
                  icon={<Icon />}
                  onClick={() => setMode(m)}
                  disabled={isDisabled}
                  title={isDisabled ? intl.formatMessage({ id: 'chat.mode.disabled.title' }) : modeDescription}
                >
                  {modeLabel}
                </ToggleButton>
              );
            })}
          </div>
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={handleReset} title={intl.formatMessage({ id: 'chat.resetButton.title' })} />
        </div>
      </div>

      <div className="chat-messages" ref={scrollRef}>
        {messages.map((msg, i) => (
          msg.role === 'system' ? (
            <div key={i} className="chat-message chat-message--system">
              <div className="chat-system-card">
                <MarkdownMessage content={msg.content} variant="full" />
              </div>
            </div>
          ) : (
            <MessageBubble
              key={i}
              role={msg.role}
              content={msg.content}
              agentLabel={intl.formatMessage({ id: MODE_CONFIG[mode].labelKey }) + ' ' + intl.formatMessage({ id: 'chat.bubble.advisor' })}
              youLabel={intl.formatMessage({ id: 'chat.bubble.user' })}
            />
          )
        ))}
        {sending && (
          <div className="chat-typing">
            <Spinner size="tiny" label={intl.formatMessage({ id: 'chat.thinking' })} />
          </div>
        )}
      </div>

      <div className="chat-input-bar">
        <Badge size="small" color={MODE_CONFIG[mode].colour} appearance="filled">{intl.formatMessage({ id: MODE_CONFIG[mode].labelKey })}</Badge>
        <Input
          className="chat-input"
          placeholder={mode === 'case' ? intl.formatMessage({ id: 'chat.input.placeholder.case' }) : intl.formatMessage({ id: 'chat.input.placeholder.general' })}
          value={input}
          onChange={(_e, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => sendMessage(input)}
          disabled={sending || !input.trim()}
        >
          {intl.formatMessage({ id: 'chat.sendButton' })}
        </Button>
      </div>
    </div>
  );
}
